import { useRef } from 'react';

import { getVirtualizationState } from './get-virtualization-state';

type Args = {
	conversationId: string | undefined;
	conversationMessagesCount: number;
	conversationMessagesResponseCount: number | null;
	isDeepLinkPending: boolean;
	olderMessageGroupsCount: number;
};

type Result = {
	isVirtualizationEnabled: boolean;
};

/**
 * Locks the chat-history virtualization decision per conversation load.
 *
 * A conversation that initially loads below the configured threshold keeps the
 * standard renderer even if it later grows past the threshold, avoiding a
 * mid-session eager-to-virtualized remount that does not help initial TTAI.
 */
export const useVirtualizationState = ({
	conversationId,
	conversationMessagesCount,
	conversationMessagesResponseCount,
	isDeepLinkPending,
	olderMessageGroupsCount,
}: Args): Result => {
	const virtualizationStateRef = useRef<{
		conversationId: string | undefined;
		isVirtualizationEnabled: boolean;
	} | null>(null);
	const shouldLockVirtualizationState =
		conversationMessagesResponseCount !== null && conversationMessagesCount > 0;

	if (virtualizationStateRef.current?.conversationId !== conversationId) {
		virtualizationStateRef.current = null;
	}

	if (shouldLockVirtualizationState && virtualizationStateRef.current === null) {
		virtualizationStateRef.current = {
			conversationId,
			isVirtualizationEnabled: getVirtualizationState({
				olderMessageGroupsCount,
				isDeepLinkPending,
			}).isVirtualizationEnabled,
		};
	}

	const lockedVirtualizationState = virtualizationStateRef.current;
	const isVirtualizationEnabled =
		lockedVirtualizationState !== null &&
		lockedVirtualizationState.conversationId === conversationId
			? lockedVirtualizationState.isVirtualizationEnabled
			: false;

	return {
		isVirtualizationEnabled,
	};
};
